import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import useSeo from '../hooks/useSeo';
import Footer from '../components/Footer';
import { 
  Globe, Brain, Languages, Network, MapPin, Award,
  Crown, Building, FileSearch, Clock, ArrowRight
} from 'lucide-react';

const FuturePage = () => {
  useSeo({
    title: 'Roadmap — What\'s Next at KnowMySchemes',
    description: 'See what we are building next at KnowMySchemes — application tracking, multilingual scheme assistance, mobile apps, and deeper AI-driven recommendations.',
    canonical: 'https://knowmyschemes.com/future',
  });
  const stats = [
    { value: '28+', label: 'States to Cover' },
    { value: '22+', label: 'Languages Planned' },
    { value: '10M+', label: 'Citizens to Help' },
    { value: '5K+', label: 'Advisors to Train' },
  ];

  const enhancements = [
    {
      icon: Globe,
      title: 'Pan-India Expansion',
      description: 'Expand support to all Indian states, ensuring every citizen has access to welfare schemes.'
    },
    {
      icon: Brain,
      title: 'AI-Powered Eligibility Checker',
      description: 'Advanced AI to automatically match users with eligible schemes instantly.'
    },
    {
      icon: Languages,
      title: 'Multilingual Interfaces',
      description: 'Launch in all major Indian languages, breaking language barriers.'
    },
    {
      icon: Network,
      title: 'Largest Welfare Network',
      description: 'Build India\'s largest welfare assistance network connecting all stakeholders.'
    },
    {
      icon: MapPin,
      title: 'Doorstep Help in Rural Areas',
      description: 'Field agents providing doorstep assistance in underserved areas.'
    },
    {
      icon: Award,
      title: 'KMS Training Program',
      description: 'Create thousands of certified welfare advisors across the country.'
    },
    {
      icon: Crown,
      title: 'Premium Membership',
      description: 'Advanced features like priority support and personalized guidance.'
    },
    {
      icon: Building,
      title: 'Regional Offices',
      description: 'Physical offices in major cities for in-person support.'
    },
    {
      icon: FileSearch,
      title: 'Document Automation',
      description: 'OCR and ML-powered document verification and matching.'
    },
    {
      icon: Clock,
      title: 'Live Status Tracking',
      description: 'Real-time tracking for all welfare applications.'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <p className="text-green-200 text-sm font-medium mb-2">Our Vision</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Building the future of welfare access
            </h1>
            <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto">
              We're just getting started. Our roadmap is designed to create India's most comprehensive and accessible welfare assistance platform, empowering millions of citizens.
            </p>
          </div>
          
          {/* Stats Banner */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3">
              <span className="text-green-200 font-bold text-lg">10 Major Initiatives</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3">
              <span className="text-green-200 font-bold text-lg">Launching 2026</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <p className="text-green-600 font-medium mb-2">Our Future Goals</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 text-center border border-gray-100">
              <p className="text-3xl md:text-4xl font-bold text-green-600 mb-2">{stat.value}</p>
              <p className="text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Image Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img 
            src="https://images.unsplash.com/photo-1639051020117-cd766c234d6c?auto=compress&cs=tinysrgb&w=800"
            alt="Young Indian professional"
            className="w-full h-64 md:h-80 object-cover"
          />
        </div>
      </div>

      {/* Future Enhancements Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Future Enhancements
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our planned initiatives to transform welfare access across India
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enhancements.map((item, index) => (
              <div 
                key={index} 
                className="bg-gray-50 p-6 rounded-xl hover:bg-green-50 transition-colors border border-gray-100 hover:border-green-200"
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-green-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Be part of this journey
          </h2>
          <p className="text-green-100 mb-8 max-w-2xl mx-auto">
            We're looking for passionate individuals to help us build this future. Join our team and make a real difference.
          </p>
          <Link 
            to="/careers"
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-green-700 px-8 py-4 rounded-xl font-semibold transition-colors"
          >
            Explore Careers
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FuturePage;
